import type {
  InterstateOnChangeEvent,
  MenuItemEvent
} from "@dealer-center/core-components";
import {
  CheckmarkSolidIcon,
  ChevronDownIcon,
  HeartSolidIcon,
  MenuButton,
  MuiIconButton,
  Typography,
  useMenu
} from "@dealer-center/core-components";
import { useNavBarStore } from "../../store";
import { backgroundColor } from "../../const";

// Interstate only reserves the icon column on menu items that actually have an
// icon, so in a menu that mixes both, the iconless labels start further left
// than the labeled ones. Handing the iconless items a hidden icon reserves the
// same column for them, lining every account name up. Hiding a real icon rather
// than sizing an empty spacer keeps the reserved width exactly right (all
// Interstate icons are 1em square, so any of them would do).
const ICON_COLUMN_SPACER = (
  <HeartSolidIcon style={{ visibility: "hidden" }} title="" />
);

// The account being viewed is marked in the icon column rather than with a "✓"
// glued to the front of its label, which would push that one account name right
// of all the others. It takes the column over from the heart on a favorite,
// since there is only one icon slot per menu item.
// `aria-hidden={false}` overrides the Interstate default so the marker is
// spoken; the item's `disabled` state alone would not say *why* it is
// unavailable.
const CURRENT_ACCOUNT_ICON = (
  <CheckmarkSolidIcon
    aria-hidden={false}
    role="img"
    title="Currently viewing"
    titleId="account-selector-current-account"
  />
);

export interface AccountSelectorProps {
  iconOnly?: boolean;
}

export const AccountSelector = ({ iconOnly }: AccountSelectorProps) => {
  const {
    selectedAccountId: selectedAccount,
    selectedAccountName,
    availableAccounts,
    setSelectedAccountId: selectAccount,
    selectedAccountStatus
  } = useNavBarStore();

  const menuReferenceProps = useMenu();
  const isSingleAccount = !availableAccounts || availableAccounts.length === 0;
  const isNothingToDo = !selectedAccount && isSingleAccount;
  if (isNothingToDo) return null;

  // Only reserve the icon column when something in the menu is actually using
  // it, so a menu of plain accounts keeps its existing flush-left labels. The
  // current-account checkmark counts: it can be the sole occupant of the column
  // when the account being viewed is not a favorite.
  const hasIconColumn = Boolean(
    availableAccounts?.some(
      account => account.icon || account.value === selectedAccount
    )
  );
  // Prefer the name the caller resolved for the selected account: the menu only
  // holds favorites and account history, so it misses the account being viewed
  // whenever that account was reached without going through either.
  const resolvedAccountName =
    selectedAccountName ||
    availableAccounts?.find(account => account.value === selectedAccount)
      ?.label ||
    selectedAccount;
  const resolvedAccountNameWithStatus = selectedAccountStatus
    ? `${resolvedAccountName} (${selectedAccountStatus})`
    : resolvedAccountName;
  const onClick = (
    event:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (isSingleAccount || !menuReferenceProps.onClick) return;
    menuReferenceProps.onClick(event);
  };

  if (iconOnly) {
    return (
      <MuiIconButton
        aria-haspopup="true"
        aria-label="Account Selector"
        color="inherit"
        id="account-selector"
        size="large"
        onClick={onClick}
      >
        <ChevronDownIcon title="Account Selector" />
      </MuiIconButton>
    );
  }

  return (
    <MenuButton
      ButtonProps={{
        id: "account-selector",
        sx: {
          // hover
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1)"
          },
          backgroundColor,
          borderColor: "rgba(255, 255, 255, 0.2)",
          borderWidth: 1,
          borderStyle: "solid"
        }
      }}
      MenuProps={{
        id: "account-selector-menu",
        menuOptions:
          availableAccounts?.map(account => ({
            value: account.value,
            onSelect: (event: InterstateOnChangeEvent<MenuItemEvent>) => {
              const selectedValue = event.target.value?.toString();
              if (selectAccount && selectedValue) selectAccount(selectedValue);
            },
            label: `${account.label} (${account.value})`,
            disabled: selectedAccount === account.value,
            // Forwarded so callers can group the menu — the account list marks
            // the last favorite to separate favorites from account history, and
            // gives every favorite a heart icon so the group reads as favorites
            // rather than as "the accounts above the line".
            divider: account.divider,
            icon:
              selectedAccount === account.value
                ? CURRENT_ACCOUNT_ICON
                : (account.icon ??
                  (hasIconColumn ? ICON_COLUMN_SPACER : undefined))
          })) || []
      }}
      aria-haspopup="true"
      aria-label="Account Selector"
    >
      <Typography
        sx={{
          cursor: !isSingleAccount ? "pointer" : "default",
          color: "white",
          display: "flex",
          fontWeight: "normal",
          alignItems: "center",
          "&>svg": {
            ml: 1
          }
        }}
        variant="h3"
      >
        {resolvedAccountNameWithStatus || "Recent Accounts"}
        {!isSingleAccount && <ChevronDownIcon title="Account Selector" />}
      </Typography>
    </MenuButton>
  );
};
